<template>
  <div id="app" class="text-sm text-base-90" :class="{'backstage-mode': isBackstage}">
    <template v-if="canUseNewUnleashMechanism">
      <template v-if="isPublicRoute">
        <LoadingScreen v-if="!hasLoadedAnonymousFlag" />
        <router-view v-else />
      </template>
      <template v-else>
        <LoadingScreen v-if="!hasLoadedAuthFlag || !isFeatureSettingsLoaded" />
        <router-view v-else />
      </template>
    </template>
    <template v-else>
      <router-view />
    </template>

    <modal-disconnection />
    <modal-invalid-token v-if="isInvalidTokenModalVisible" />

    <modal-error
      v-if="errorOnInternalServer !== null"
      :title="errorOnInternalServer"
      @hide="handleHideErrorModal"
    />

    <BackgroundEnlargeImage />

    <!-- This component can be located anywhere in your App. -->
    <!-- The slot content of the above portal component will be rendered here. -->
    <portal-target id="modal-destination" name="modal-destination" multiple />
    <portal-target name="context-menu-destination" />
    <portal-target name="report-panel-destination" style="top: 4rem;" />

    <!-- Global Chatbot Iframe — mounted OUTSIDE <router-view> (and reparented to
         <body> on mount), so an open panel + its conversation survive route
         changes. Opened by AssistantHeaderButton and AssistantLauncherButton,
         which share assistantTrigger.mixin. -->
    <GlobalChatbotContainer v-if="canUseAIChatbot && currentUser?.id" :hasCloseButton="hasChatbotCloseButton" />

    <!-- The floating assistant launcher (bottom-right). The component holds the
         real gate (assistantTrigger.mixin); this v-if is just a coarse prefix so
         the async chunk is never fetched on the flag-off / logged-out path. -->
    <AssistantLauncherButton v-if="canUseAssistantLauncher" />

    <!-- Onboarding checklist step tracker — renderless, persists across all routes -->
    <OnboardingIkeaChecklistTracker v-if="currentUser?.id" />

    <!-- load crazy egg when has current user -->
    <SurveyCrazyEgg v-if="currentUser.id" />
    <AIDocToQuizGeneratingBox v-if="docToSlideUniqueRequestId" />
    <ImportStatusBox v-if="isImportStatusBoxVisible" />
    <ImportStatusToaster />
    <!-- AHA2-171: redesigned import-progress notification (self-gates on the
         release-AHA2-171-import-loading-redesign flag; renders nothing when off). -->
    <ImportProgressNotification />

    <PowerpointOnlineNotSupport v-if="isEnableAddinPowerpoint && isOfficeOnline && !canUsePptOnline" />

    <TemplateLibraryModal
      v-if="canUseTemplateLibraryAnywhere && isShowModalTemplateLibrary"
      v-aha-emit-action.view
      name="template-library-modal-app-level"
    />

    <!-- Import a quiz spreadsheet into a brand-new presentation (triggered outside the editor, e.g. Home) -->
    <ImportSheetNewPresentation v-if="openModalImportSheetNewPresentation" />
    <OnboardingOverlay />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import sentryService from '@/services/sentry'
import EventBus from '@/eventBus'
import socket from '@/socket'
import Config from '@/config'
import ImportStatusConstant from '@/constant/importStatus'
import { DOCKED_HTML_CLASS } from '@/constant/assistantDock'

// utils
import utils from '@/util'
import promotionCodeUtils from '@/utils/promotionCodeUtils'
import office from '@/services/office'
import zoho from '@/services/zoho'
import SurveyUtils from '@/utils/survey'

// components
import BackgroundEnlargeImage from '@/components-v2/presentation-editor/middle/BackgroundEnlargeImage.vue'
import ModalDisconnection from '@/components-v2/modals/Disconnection.vue'
import ImportStatusToaster from '@/components-v2/presentation-editor/side-right/ImportStatusToaster.vue'
import ImportProgressNotification from '@/components-v2/presentation-editor/side-right/ImportProgressNotification.vue'
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'
// api
import Api from '@/api'
import PresentationApi from '@/api/presentation'

// services
import cookie, { FIRST_PAGE_COOKIE } from '@/services/cookie'
import { initIndexedDB } from './utils/indexedDB/indexed.db'
import constant from './constant'
import * as localStorageExpiry from '@/utils/local.storage.expiry'

import { PUBLIC_ROUTES, EXCLUDED_SHOWING_TOASTS_ROUTES, ROUTE_NAMES } from './constant/routes'

import AppSocketHandler from './utils/AppSocketHandler'
import { flags } from '@/utils/abtest.utils'
import { isEmbedded } from '@/vuex/modules/embeddable/embeddable.utils'

sentryService.init()
initIndexedDB((e) => {
  Sentry.captureException(e)
})

export default {
  components: {
    LoadingScreen,
    ModalDisconnection,
    ModalError: () => import('@/components-v2/modals/Error.vue'),
    ModalInvalidToken: () => import('@/components-v2/modals/InvalidToken.vue'),
    BackgroundEnlargeImage,
    SurveyCrazyEgg: () => import('@/components-v2/common/survey-crazy-egg/SurveyCrazyEgg.vue'),
    AIDocToQuizGeneratingBox: () => import('@/components-v2/presentation-editor/new-slide/OpenAI/AIDocToQuizGeneratingBox.vue'),
    ImportStatusBox: () => import('@/components-v2/presentation-editor/side-right/ImportStatusBox.vue'),
    GlobalChatbotContainer: () => import('@/components-v2/common/GlobalChatbotContainer.vue'),
    AssistantLauncherButton: () => import('@/components-v2/unified-assistant/AssistantLauncherButton.vue'),
    ImportStatusToaster,
    ImportProgressNotification,
    OnboardingIkeaChecklistTracker: () => import('@/components-v2/onboarding/OnboardingIkeaChecklistTracker.vue'),
    PowerpointOnlineNotSupport: () => import('@/components-v2/my-presentations/PowerpointOnlineNotSupport.vue'),
    TemplateLibraryModal: () => import('@/components-v2/template-library/v2/TemplateLibraryModal.vue'),
    ImportSheetNewPresentation: () => import('@/components-v2/modals/ImportSheetNewPresentation.vue'),
    OnboardingOverlay: () => import('@/components-v2/onboarding/OnboardingOverlay.vue'),
  },
  data() {
    return {
      isCannotLoadUser: true,
    }
  },
  computed: {
    ...mapState({
      presentations: (state) => state.presentations,
      presentation: (state) => state.presentation,
      slideActive: (state) => state.slideActive,
      isScreenCapture: (state) => state.isScreenCapture,
      currentUser: (state) => state.currentUser,
      errorOnInternalServer: (state) => state.errors.errorOnInternalServer,
      isPreview: (state) => state.isPreview,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
      docToQuizResult: (state) => state.docToQuizResult,
      docToQuizUniqueRequestId: (state) => state.docToQuizUniqueRequestId,
      integrationPlatform: (state) => state.integrationPlatform,
      importStatus: (state) => state.importStatus,
      presenterLocale: (state) => state.presenterLocale,
      isRenderedInsideIframe: (state) => state.isRenderedInsideIframe,
      isOfficeOnline: (state) => state.isOfficeOnline,
      isShowModalTemplateLibrary: (state) => state.isShowModalTemplateLibrary,
      openModalImportSheetNewPresentation: (state) => state.openModalImportSheetNewPresentation,
    }),
    ...mapGetters(['isBackstage', 'isPresenting', 'isPresentationEditorRoute']),
    ...mapGetters('rightNav', ['rightNavActiveTab']),
    ...mapGetters('collaboration', ['isTakeOverToolbarVisible']),
    ...mapGetters('docToSlideGenerator', [
      'docToSlideUniqueRequestId',
      'docToSlideResponse',
    ]),
    ...mapGetters('aiChatbot', ['canUseAIChatbot', 'canUsePresenterToolBridge']),
    ...mapGetters('zoomApp', ['isZoomReady']),
    ...mapGetters('presentationEditor', ['isBackstageRightColumnCollapsed']),
    ...mapGetters(['getPresenterLocale']),
    ...mapGetters('featureToggles', ['hasLoadedAnonymousFlag', 'hasLoadedAuthFlag', 'canUseTemplateLibraryAnywhere', 'canUsePptOnline']),
    ...mapGetters('featureSettings', ['isFeatureSettingsLoaded']),
    canUseNewUiBackStage() {
      return this.$store.getters['featureToggles/canUseNewUiBackStage']
    },

    canUseNewUnleashMechanism() {
      return flags.canUseNewUnleashMechanism
    },
    isPublicRoute() {
      const routeName = this.$route && this.$route.name
      return PUBLIC_ROUTES.includes(routeName)
    },
    isInvalidTokenModalVisible() {
      if (this.integrationPlatform === 'GoogleSlide') {
        return false
      }
      const routeName = this.$route && this.$route.name

      return !PUBLIC_ROUTES.includes(routeName)
    },
    isImportStatusBoxVisible() {
      const status = [ImportStatusConstant.IMPORT_PPT_UPLOADING, ImportStatusConstant.IMPORT_PPT_CONVERTING]
      return status.includes(this.importStatus)
    },
    hasChatbotCloseButton() {
      return this.canUseAIChatbot && this.isPresentationEditorRoute(this.$route)
    },

    // The single condition under which the assistant actually replaces Zoho SalesIQ.
    // Deliberately shared between the launcher's v-if and the Zoho kill switch: they
    // used to be written out separately, and the kill switch was the SHORTER of the
    // two. Anyone the bridge flag reached but the launcher did not — every logged-out
    // visitor, since zoho.init() runs on public routes regardless of auth — lost
    // support chat and got no assistant in exchange. One expression, no drift.
    canUseAssistantLauncher() {
      return this.canUsePresenterToolBridge && this.canUseAIChatbot && !!this.currentUser?.id
    },
  },
  watch: {
    $route: {
      immediate: false,
      handler() { this.consumeOpenTemplatesQueryIfPresent() },
    },
    // Bridge ON: our assistant replaces Zoho SalesIQ, so suppress Zoho's bubble
    // globally. The body class survives Zoho re-init on navigation; hideChatBtn()
    // is the SDK belt-and-suspenders. The flag is the kill switch, so the OFF
    // transition must fully reverse the suppression — otherwise the display:none
    // body classes win and support chat is unreachable until a reload.
    // Keyed on canUseAssistantLauncher, NOT on the bridge flag alone: suppress Zoho
    // only where the assistant is actually rendered to replace it.
    canUseAssistantLauncher: {
      immediate: true,
      handler(canSuppress) {
        try {
          if (canSuppress) {
            // `hide-zoho-float` is bridge-only and hides `#zsiq_float`; kept off the
            // shared `hide-zoho-chat` class so GoogleSlide/embed flag-OFF stays staging.
            document.body.classList.add('hide-zoho-chat', 'hide-zoho-float')
            zoho.hideChatBtn()
            return
          }
          // Flag flipped OFF at runtime: undo only what the bridge itself set.
          // `hide-zoho-float` is bridge-exclusive, so its presence is the marker
          // that the bridge was ON — gating on it keeps the immediate:true pass at
          // load (flag already off) a no-op, so flag-OFF stays byte-for-byte
          // staging. `hide-zoho-chat` is shared: GoogleSlide (created()) and embed
          // (EmbedAppContainer) own their own copy and keep Zoho hidden there on
          // staging too, so restore the bubble only on the plain presenter surface
          // — otherwise we'd stomp those owners and force Zoho visible.
          if (!document.body.classList.contains('hide-zoho-float')) return
          document.body.classList.remove('hide-zoho-float')
          if (this.integrationPlatform !== 'GoogleSlide' && !isEmbedded()) {
            document.body.classList.remove('hide-zoho-chat')
            // restoreChatBtn, not showChatBtn: hideChatBtn() hid chatbutton,
            // floatbutton, chatwindow AND floatwindow, but showChatBtn() re-shows
            // only floatbutton — so the "kill switch" could not actually be
            // un-flipped without a reload. See services/zoho.js.
            zoho.restoreChatBtn()
          }
        } catch (error) {
          Sentry.captureException(error)
        }
      },
    },
    canUseTemplateLibraryAnywhere(newVal, oldVal) {
      if (newVal && !oldVal) this.consumeOpenTemplatesQueryIfPresent()
    },
    isPresenting: function handleIsPresentingChange(isPresenting) {
      this.checkWindowResize()
      if (!isPresenting) {
        SurveyUtils.removeStyleDisplayNoneToSurvey()
        this.setRightNavActiveTabAction('')
      }
    },

    isPreview: function watchIsPreviewMode(isPreview) {
      if (!isPreview) return
      SurveyUtils.addStyleDisplayNoneToSurvey() // to prevent survey showing in editor still shows in preview mode
    },
  },
  async created() {
    this.initializeFeatureToggles()
    const firstUrlQueryString = window.location.search
    utils.migrateTokenCookie()

    const hasMSTeamsInPathOrParam = window.location.href.includes('ms-teams') || window.location.href.includes('ms_teams')
    if (hasMSTeamsInPathOrParam) {
      const { app } = await import('@microsoft/teams-js')
      await app.initialize()
      app.notifySuccess()
      this.$store.state.isMSTeamsApp = true
    }

    this.handleUsersAdsCampaignAction(firstUrlQueryString)

    if (this.integrationPlatform === 'GoogleSlide') {
      document.body.classList.add('hide-zoho-chat')
    }
  },
  async mounted() {
    try {
      document.body.classList.add('loaded-app')
      if (this.isEnableAddinPowerpoint && window.location.href.indexOf('/presentation/') === -1) {
        office.loadOffice('App.vue')
      }
    } catch (error) {
      Sentry.captureException(error)
    }
    this.consumeOpenTemplatesQueryIfPresent()
    this.setUserFirstPage()
    const urlParams = new URLSearchParams(window.location.search)
    const isAdmin = urlParams.get('admin')
    const promotionCode = urlParams.get('promotion_code')
    if (promotionCode) {
      promotionCodeUtils.setPromotionCode(promotionCode)
    }

    // setup vue resource in order to make AJAX requests
    Api.initResources()

    this.$store.state.isPresenterWhiteLabel = utils.checkWhiteLabelDomain()
    this.onVisibilityChange()

    const ahaUserIdFromCookie = utils.getRenderedUserId()

    socket.initialize(ahaUserIdFromCookie)
    setInterval(() => {
      socket.emit('server_timestamp')
    }, 60000)
    socket.emit('server_timestamp')

    EventBus.$on('detectScreen', (force) => this.checkWindowResize(force))
    EventBus.$on('handleAfterLoadedUser', (user) => {
      this.handleInitAfterLoadedUser(user)
    })
    window.addEventListener('resize', () => {
      this.checkWindowResize()
      if (this.canUseNewUiBackStage) {
        setTimeout(() => {
          this.checkWindowResize()
        }, 300)
      }
    })

    if (this.canUseNewUnleashMechanism) {
      this.initUnleash()
    }

    if (isAdmin) { // LinhVu: skip get listen to socket if admin
      this.$store.state.isAdmin = true
      if (!this.canUseNewUnleashMechanism) {
        // Huey: I think skip here is pointless, because the socket init is called anyway after emitted by getCurrentUserAction.
        // see the EventBus.$on('handleAfterLoadedUser', (user) => { ... }) above.
        return
      }
    }

    if (this.$store.state.isPresenterWhiteLabel) {
      utils.mixpanelTracking('White Label', 'Open', `Host: ${window.location.host}`)
    }

    if (!utils.isLoggedIn()) {
      if (this.integrationPlatform === 'GoogleSlide' && window.location.href.indexOf('google-redirect') === -1 && window.location.href.indexOf('google-auth') === -1) {
        this.$router.push({ name: 'Login' })
      }

      if (!this.canUseNewUnleashMechanism) {
        this.initUnleash()
      }

      return
    }

    // not get current user when open token page
    const pathAuthRedirects = ['/pages/token', '/auth/?provider']
    if (pathAuthRedirects.some((path) => window.location.href.indexOf(path) !== -1)) {
      return
    }
    let user
    try {
      user = await (flags.canUseCacheProfileOnLoad ? this.getCurrentUserWithCacheAction() : this.getCurrentUserAction())
    } catch (err) {
      // 401 is handled by the AhaInterceptor in main.ts (InvalidToken modal + auto-logout).
      // Skip the redirect for integration platforms — they have their own auth/host flows.
      // Skip when embedded as an iframe — the host page owns the failure UX.
      if (err?.status === 401 || this.integrationPlatform || isEmbedded()) {
        throw err
      }
      this.$router.replace({
        name: ROUTE_NAMES.SERVICE_UNAVAILABLE,
        query: { from: this.$route.fullPath },
      })
      return
    }
    if (!user && this.integrationPlatform === 'GoogleSlide') {
      this.$router.push({ name: 'Login' })
      return
    }
    this.handleInitAfterLoadedUser(user)

    this.clearExpiredLocalStorage()
    if (this.integrationPlatform === 'GoogleSlide') {
      const presenting = utils.getParamValue('presenting')
      if (!presenting) {
        this.$router.push({ name: 'IntegrationGoogleSlide' })
      }
    }
  },
  beforeDestroy() {
    this.offVisibilityChange()
  },
  destroyed() {
    AppSocketHandler.disconnectSocket(this)
    EventBus.$off('export-data:success')
    EventBus.$off('export-excel:success')
  },
  methods: {
    ...mapActions('featureToggles', ['initializeFeatureToggles', 'updateFeatureContext', 'startUnleash']),
    ...mapActions([
      'logOutAccountRemoval',
      'setErrorOnInternalServerAction',
      'setCurrentUserAction',
      'getCurrentUserAction',
      'setPresenterLang',
    ]),
    ...mapActions('userAdsCampaign', [
      'handleUsersAdsCampaignAction',
    ]),
    ...mapActions(['updateCurrentUserAction', 'setLoadingExportPdfAction', 'setLoadingExportImageAction', 'setLoadingExportExcelAction']),
    ...mapActions('rightNav', ['setRightNavActiveTabAction']),
    ...mapActions('pptAddin', ['startActiveSlidePPT']),
    ...mapActions('loadingPage', ['getCurrentUserWithCacheAction']),
    consumeOpenTemplatesQueryIfPresent() {
      if (!this.canUseTemplateLibraryAnywhere) return
      const query = this.$route.query || {}
      if (query.openTemplates !== '1') return

      let candidate = query.category
      if (Array.isArray(candidate)) candidate = candidate.find((v) => typeof v === 'string' && v.trim())
      if (typeof candidate !== 'string') candidate = null
      if (candidate) {
        candidate = candidate.trim()
        if (!candidate || candidate.length > 128) candidate = null
      }

      this.$store.dispatch('setShowModalTemplateLibraryAction', {
        visible: true,
        initialCategoryId: candidate || null,
        deepLinkSource: this.$route.fullPath,
      })

      const { openTemplates: _ot, category: _cat, ...rest } = query
      this.$router.replace({ query: rest })
    },
    initUnleash() {
      this.updateFeatureContext({
        presenterLanguage: this.presenterLocale,
      })
      this.startUnleash()
    },
    handleInitAfterLoadedUser(user) {
      if (!this.isCannotLoadUser) return
      this.isCannotLoadUser = false
      this.initializeSocket(user)
      this.subscribeToSocketEvents(user)
      AppSocketHandler.initSocket(`global--user-${user.id}`, this)
      this.checkUserHasHostedEvents()
      utils.ga4Config({
        user_id: user.id,
      })
      this.setFlagUserUpdatedGA4(user)

      this.registerExportEventListeners()

      this.syncPlanKeyCookie(user.plan)
    },
    syncPlanKeyCookie(planType) {
      utils.syncPlanKeyCookie(planType)
    },
    showReportGenerationSuccessMessage() {
      if (EXCLUDED_SHOWING_TOASTS_ROUTES.includes(this.$route.name)) {
        return
      }
      setTimeout(() => {
        this.$toasted.clear()
        utils.showToastSuccess(this.$i18nPresenter.t('Export completed'))
      }, 500)
    },
    showReportGenerationErrorMessage(errorMessage) {
      if (EXCLUDED_SHOWING_TOASTS_ROUTES.includes(this.$route.name)) {
        return
      }
      setTimeout(() => {
        this.$toasted.clear()
        utils.showToastError(`${errorMessage} ${this.$i18nPresenter.t('Please refresh the page and try again.')}`, null, null, {
          duration: 1000000,
          action: {
            text: 'Close',
            onClick: () => {
              this.$toasted.clear()
            },
          },
        })
      }, 500)
    },
    showReportGenerationUpdateProgressMessage(percentageProgress) {
      // skip for now to improve the UI display later
      return percentageProgress
      // setTimeout(() => {
      //   this.$toasted.clear()
      //   utils.showToastInfo(this.$i18nPresenter.t(`Report generation in progress ${percentageProgress}%`))
      // }, 500)
    },
    initializeSocket(user) {
      socket.emit('join-user', `user-${user.id}`)
      socket.emit('join-user', 'ahaslides-presenter')
      socket.emit('join-user', `global--user-${user.id}`)
    },

    subscribeToSocketEvents(user) {
      socket.subscribe('export-data', (data) => {
        this.handleEmitEventExport(data)
      })
      socket.subscribe(`user-${user.id}`, ({
        action, type, socketId, data,
      }) => {
        if (type === 'user' && action === 'accountRemoval') {
          // in case presenter is opening multiple tabs
          // the active tab must not log out in order to show comfirmation popup
          if (socketId === socket.getSocketId()) return
          this.logOutAccountRemoval()
        }
        if (type === 'notification' && action === 'new-notification') {
          this.$store.state.notifications.unshift(data)
          this.$store.state.notificationUnread++
        }
      })
      socket.subscribe('ahaslides-presenter', ({
        action, type, data,
      }) => {
        if (type === 'notification' && action === 'new-notification') {
          if (Object.prototype.toString.call(data) === '[object Object]') {
            this.$store.state.notifications.unshift(data)
            this.$store.state.notificationUnread++
          } else {
            // eslint-disable-next-line array-callback-return
            data.map((obj) => {
              if (obj.createdForTeam === 0 && (!this.currentUser.accountId ||
                (this.currentUser.accountRole !== 'owner' && this.currentUser.accountRole !== 'admin'))) {
                return
              }
              this.$store.state.notifications.unshift(obj)
              this.$store.state.notificationUnread++
            })
          }
        }
      })
    },

    registerExportEventListeners() {
      EventBus.$on('export-pdf:success', () => {
        this.setLoadingExportPdfAction(false)
        this.setLoadingExportImageAction(false)
        utils.trackExportCompleted('pdf')
        this.showReportGenerationSuccessMessage()
      })

      EventBus.$on('export-image:success', () => {
        this.$store.state.loadingExportImage = false
        this.setLoadingExportImageAction(false)
        this.setLoadingExportPdfAction(false)
        utils.trackExportCompleted('image')
        this.showReportGenerationSuccessMessage()
      })

      EventBus.$on('export-excel:success', () => {
        this.setLoadingExportExcelAction(false)
        utils.trackExportCompleted('excel')
        this.showReportGenerationSuccessMessage()
      })
    },

    handleEmitEventExport(doc) {
      // Avoid duplicated handler inside iframe.
      // We put it here to reuse the handle logic in entire App.
      // But it has a flaw that every iframe (include sharing view) will listen and handle the event.
      if (this.isRenderedInsideIframe) return
      const { exportType } = doc

      if (doc.type == 'export-update-progress') {
        this.showReportGenerationUpdateProgressMessage(doc.exportData.progress || 0)
      } else if (doc.type == 'export-status') {
        this.$store.dispatch('onlyPatchPresentationAction', { exportData: doc.exportData })
        const isExportImage = exportType === 'image' && doc.exportData.status === 'success'
        const isExportPdf = exportType === 'pdf' && doc.exportData.status === 'success'
        const isExportExcel = exportType === 'excel' && doc.exportData.excelStatus === 'success'
        if (isExportImage) {
          utils.downloadFile(doc.exportData.zipUrl)
          EventBus.$emit('export-image:success', doc.exportData)
          this.$store.state.loadingExportImage = false
        }
        if (isExportPdf) {
          utils.downloadFile(doc.exportData.pdfUrl)
          EventBus.$emit('export-pdf:success', doc.exportData)
          this.$store.state.loadingExportPdfV2 = false
        }
        if (isExportExcel) {
          utils.downloadFile(doc.exportData.excelUrl)
          EventBus.$emit('export-excel:success', doc.exportData)
          this.$store.state.loadingExportExcelV2 = false
          this.$store.state.loadingExportExcelV5 = false
        }
      } else {
        let errorMessage = this.$i18nPresenter.t('Unable to download slides.')
        errorMessage = ['pdf', 'image'].includes(exportType)
          ? this.$i18nPresenter.t('Export failed.')
          : errorMessage
        // Clear the loading flag matching the failed export type, otherwise an
        // Excel failure would leave its spinner running indefinitely.
        if (exportType === 'excel') {
          this.setLoadingExportExcelAction(false)
        } else {
          this.setLoadingExportPdfAction(false)
          this.setLoadingExportImageAction(false)
        }
        utils.trackExportFailed(exportType)
        this.showReportGenerationErrorMessage(errorMessage)
      }
    },

    setFlagUserUpdatedGA4(user) {
      if (!user || user.isGuest) {
        return
      }
      const keyFlag = `updated-ga4-${user.id}`
      const updatedGA4 = localStorageExpiry.getWithExpiry(keyFlag)
      if (!updatedGA4) {
        utils.ga4Set('user_properties', {
          first_page: user.firstPage,
          referral_user_id: user.referralUserId,
          sign_up_source: user.source,
          use_ahaslides_for: user.personalInformation ? user.personalInformation.useAhaSlidesFor : null,
          updated_at: user.updatedAt,
        })
        localStorageExpiry.setWithExpiry(keyFlag, true, 30 * 24 * 60 * 60 * 1000)
      }

      const keyFlagUpdateSource = `updated-ga4-user-source-${user.id}`
      const updatedUserSourceGA4 = localStorageExpiry.getWithExpiry(keyFlagUpdateSource)
      if (!updatedUserSourceGA4) {
        utils.ga4Set('user_properties', {
          sign_up_source: user.source,
        })
        localStorageExpiry.setWithExpiry(keyFlagUpdateSource, true, 3650 * 24 * 60 * 60 * 1000)
      }
    },
    setUserFirstPage() {
      const firstPage = utils.getFirstPage()
      // Written through setValue, so it carries the region prefix on EU.
      const isGuest = cookie.getValue(constant.storageKeys.AHA_GUEST_USER)
      if (firstPage || (utils.isLoggedIn() && !isGuest)) {
        return
      }
      cookie.setRootDomainCookie(FIRST_PAGE_COOKIE, window.location.href)
    },

    _setWidthForContentSlideToolbar(canvasWidth) {
      if (this.slideActive.type === 'freestyle') {
        const toolbar = document.getElementById('content-slide-toolbar-wrp')
        if (toolbar) {
          toolbar.style.padding = `0 calc((100% - ${canvasWidth}px) / 2)`
        }
      }
    },

    tweakCanvasSizeForPPT(width) {
      if (!this.isEnableAddinPowerpoint) return width
      const LeftNavWidth = 225
      const RightNavWidth = 0
      const PaddingAndMargin = 16
      const fixedWidthPPT = 680
      const newWidth = window.innerWidth - LeftNavWidth - RightNavWidth - PaddingAndMargin
      if (newWidth < fixedWidthPPT) return fixedWidthPPT
      return newWidth
    },

    // The width the app actually lays out in. The docked panel SHRINKS #app via
    // CSS without moving window.innerWidth, so measure #app's real width (keeping
    // the CSS as the single source of truth for panel width + breakpoints).
    appViewportWidth() {
      // Bridge OFF: nothing narrows #app, so viewport width IS the app width —
      // exactly as origin/staging read it.
      if (!this.canUsePresenterToolBridge) return window.innerWidth
      // Bridge ON but panel CLOSED: still nothing narrows #app, so stay on
      // window.innerWidth. #app.clientWidth would differ from it by the document
      // scrollbar (~15px) — invisible in the >/< comparisons below, but fatal to
      // checkWindowResize's `innerWidth === 1080` EXACT equality, which would then
      // never fire and leave the editor canvas 382px narrower than with the flag off.
      if (typeof document !== 'undefined'
        && !document.documentElement.classList.contains(DOCKED_HTML_CLASS)) {
        return window.innerWidth
      }
      const app = document.getElementById('app')
      const width = app && app.clientWidth
      return width > 0 ? width : window.innerWidth
    },

    tweakCanvasSizeForMobileTablet(width) {
      const appWidth = this.appViewportWidth()
      if (appWidth > 1180) return width
      let LeftNavWidth = 225
      const RightNavWidth = 98
      const PaddingAndMargin = 16
      const fixedWidthPPT = 560
      if (appWidth > 960) {
        LeftNavWidth = 235
      }

      const newWidth = appWidth - LeftNavWidth - RightNavWidth - PaddingAndMargin
      if (newWidth < fixedWidthPPT) return fixedWidthPPT
      return newWidth
    },

    checkWindowResize(force = false) {
      // let wHeight = window.innerHeight - (window.innerHeight / 100) * 8;

      EventBus.$emit('window-resizeing')
      let height
      let width
      const mainSlide = document.getElementById('main-slide')
      const containerSlide = document.getElementById('slide-container')
      // const slideNotes = document.getElementById('aha-slide-notes')
      let scale = 1
      const fixWidth = 1280
      const fixHeight = 720
      let currentWidth
      let currentHeight
      // Layout width is #app's width, not the viewport's (the docked panel narrows
      // #app without touching window.innerWidth).
      let innerWidth = this.appViewportWidth()
      if (innerWidth < 960 && !this.isPresenting) {
        innerWidth = 860
      }

      if (!mainSlide) return
      this.$store.state.currentSlideCanvas = 'screen-hd'

      if (!this.$store.getters.isPresenting) {
        // in editor mode, set correct height so that
        // canvas + buttons + notes fit into the screen without scrolling
        const HEADER_HEIGHT = 64
        // const BUTTONS_PLUS_NOTES_HEIGHT = (slideNotes && (slideNotes.offsetHeight + 90)) || 180
        const BUTTONS_PLUS_NOTES_HEIGHT = 180
        const OFFSET = 10
        const TAKE_OVER_TOOLBAR_HEIGHT = !this.isPresentingPpt ? 76 : 0
        height = window.innerHeight - BUTTONS_PLUS_NOTES_HEIGHT
         - HEADER_HEIGHT - OFFSET - TAKE_OVER_TOOLBAR_HEIGHT
        const BUTTONS_PPT = 44

        width = innerWidth - 652
        if (this.rightNavActiveTab) {
          width -= 90
          height += 50
        } else if (innerWidth === 1080 && !this.isPresenting) {
          width += 382
        }
        width = this.tweakCanvasSizeForMobileTablet(width)
        if (this.isEnableAddinPowerpoint) {
          height += BUTTONS_PLUS_NOTES_HEIGHT - BUTTONS_PPT
        }
        this._setWidthForContentSlideToolbar(width)
      } else {
        height = window.innerHeight
        width = innerWidth
      }

      if (this.$store.state.sharePresentation && !utils.getParamValue('hideText', window.location.href)) {
        height -= 100
      }

      if (this.isBackstage) {
        if (this.canUseNewUiBackStage) {
          const realWidth = window.innerWidth
          const realHeight = window.innerHeight

          const isMini = realWidth < 1200 || realHeight < 750
          if (this.$store.state.isMiniBackstage !== isMini) {
            this.$store.state.isMiniBackstage = isMini
          }

          const offsetH = isMini ? 260 : 340
          const offsetHMobile = isMini ? 400 : 420

          if (realWidth < 960) {
            width = realWidth - 32
            height = realHeight - offsetHMobile
          } else {
            const widthOffset = this.isBackstageRightColumnCollapsed ? 240 : 420
            width = realWidth - widthOffset
            height = realHeight - offsetH
          }
        } else {
          const HEADER_HEIGHT = 60
          const CANVAS_PADDING_Y = 24
          const NOTE_HEIGHT = 240
          width = innerWidth - 420
          height = window.innerHeight - (HEADER_HEIGHT + CANVAS_PADDING_Y + NOTE_HEIGHT)
        }
      }

      if (mainSlide.classList.contains('has-resizable-slide-notes')) {
        const slideCell = document.getElementById('slide-cell')
        if (slideCell && slideCell.clientHeight > 0) {
          height = Math.min(height, slideCell.clientHeight)
        } else {
          const slideNotes = document.getElementById('aha-slide-notes')
          if (slideNotes) {
            height -= slideNotes.offsetHeight
          }
        }
      }

      const POWERPOINT_ADDIN_HEADER_HEIGHT = 36
      // AHA-46671: while presenting inside the add-in, EditorHeaderWrapper is not
      // rendered (`v-if="... && !isPresentingPpt"`), so reserving its height here
      // shrank the whole 16:9 canvas and left black margins on every side.
      if (this.isEnableAddinPowerpoint && !this.isPresenting) {
        height -= POWERPOINT_ADDIN_HEADER_HEIGHT
      }

      const ratio = width / height
      if (ratio > 16 / 9) {
        currentWidth = (height * 16) / 9
        currentHeight = height
      } else {
        currentHeight = (width * 9) / 16
        currentWidth = width
      }

      scale = currentHeight / fixHeight
      mainSlide.style.width = `${currentWidth}px`
      containerSlide.style.width = `${fixWidth}px`
      containerSlide.style.transform = `scale(${scale})`
      this.$store.state.slideContainerScale = scale
      if (force || (!this.isScreenCapture || this.slideActive.type !== 'openEnded')) {
        mainSlide.style.height = `${currentHeight}px`
        containerSlide.style.height = `${fixHeight}px`
      }
    },

    handleHideErrorModal() {
      if (this.errorOnInternalServer === constant.errors.GOOGLE_LOGIN_ERROR) {
        this.$router.replace({ name: 'Login' })
      }
      if (this.errorOnInternalServer === constant.errors.SKOLETUBE_LOGIN_ERROR) {
        this.$func.go(Config.skoletubeHomepage)
      }
      this.setErrorOnInternalServerAction(null)
    },

    async checkUserHasHostedEvents() {
      if (!this.currentUser.firstEventStartedAt) {
        const numOfHostedEvents = await PresentationApi.countHostedEvents()
        if (numOfHostedEvents > 0) {
          this.$store.state.userHasHostedEvents = true
        }
      }
    },
    _isStaleAccount() {
      // Backdoor renders someone other than the cookie's owner by design, so this would reload forever.
      if (this.currentUser.backdooredBy) {
        return false
      }
      const cookieUserId = utils.getUserId()
      return cookieUserId && String(cookieUserId) !== String(this.currentUser.id)
    },
    _triggerReloadToSyncLoginState() {
      if (document.hidden) return

      if (this.isEnableAddinPowerpoint) {
        office.reCheckActiveView()
        this.startActiveSlidePPT('Reload sync')
        return
      }

      // AHA-36894: Skip reload logic for Zoom app to prevent infinite reloading
      if (this.isZoomReady || window.location.href.includes('/apps/zoom')) {
        return
      }

      // browser tab is active
      const isLoggedIn = utils.isLoggedIn()
      const hasUser = this.currentUser && this.currentUser.id

      // if user is logged out in another tab
      if (!isLoggedIn && hasUser) {
        window.location.reload()
      }

      // if another tab signed in as someone else
      if (isLoggedIn && hasUser && this._isStaleAccount()) {
        window.location.reload()
        return
      }

      // if user is logged in another tab
      if (isLoggedIn && !hasUser) {
        window.location.reload()
      }
    },
    onVisibilityChange() {
      document.addEventListener('visibilitychange', this._triggerReloadToSyncLoginState)
    },
    offVisibilityChange() {
      document.removeEventListener('visibilitychange', this._triggerReloadToSyncLoginState)
    },
    clearExpiredLocalStorage() {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key.includes('chat-messages-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            if (data.expiry) {
              localStorageExpiry.getWithExpiry(key)
            } else {
              localStorage.removeItem(key)
            }
          } catch (error) {
            Sentry.captureException(error)
          }
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.backstage-mode {
  &::v-deep {
    .editor-header {
      background-color: #191F2D;
      .presentation-title-container .text-secondary-100, .backstage-buttons .tab-trigger-btn:hover {
        color: #ffffff;
      }
      .presentation-title-container {
        cursor: default;
      }
      .backstage-buttons .tab-trigger-btn {
        color: #B4BCCF;
      }
      .private-mode-button {
        background-color: #3F4557;
        .private-mode-icon {
          color: #ffffff;
        }
      }
    }
    .aha-left-column {
      background-color: #282E3E;
      border-color: #3F4557;
      .list-group-item {
        &.active-slide, &.select-slide {
          background-color: #191F2D;
          .aha-slide-order {
            color: #6A1EBB;
          }
        }
        .aha-slide-order {
          color: #ffffff;
        }
      }
    }
    .aha-middle, .aha-slide-notes .notes-input {
      background-color: #282E3E;
      border-color: #3F4557;
      color: #ffffff;
    }
    .aha-slide-notes {
      height: 240px;
      .notes-input {
        font-size: 20px;
        line-height: 1.25;
      }
    }
  }
}
</style>
